import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { logger } from '@/lib/logger';
import { Annotation } from '@/services/annotationService';
import { useAnnotations } from '@/hooks/useAnnotations';
import { useAppStore } from '@/stores/appStore';
import { wrapTouchEvent } from './touchEventWrapper';
import { useUndoRedoStore } from '@/stores/undoRedoStore';

interface TextAnnotationProps {
  annotation: Annotation;
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
}

export function TextAnnotation({
  annotation,
  isSelected,
  isHovered,
  scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  isInteractive,
}: TextAnnotationProps) {
  const { data, style } = annotation;

  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(data.text || '');
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(data.rotation || 0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { updateAnnotation, deleteAnnotation } = useAnnotations(annotation.floor_plan_id);
  const setIsEditingText = useAppStore(state => state.setIsEditingText);
  const addAction = useUndoRedoStore(state => state.addAction);
  const x = data.x!;
  const y = data.y!;
  const text = data.text || '';
  const width = data.width || 200;
  const height = data.height || 50;
  const rotation = data.rotation || 0;

  // Update local rotation angle when annotation changes
  useEffect(() => {
    if (!isRotating) {
      setRotationAngle(rotation);
    }
  }, [rotation, isRotating]);

  // Keep font size constant regardless of zoom
  const fontSize = style.fontSize || 16;
  const padding = 4;

  // Function to wrap text based on width using canvas for text measurement
  const wrapText = useCallback(
    (text: string, maxWidth: number): string[] => {
      if (!text) return [''];

      // Use canvas to measure text width (more efficient than SVG)
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return [text];

      context.font = `${style.fontWeight || 'normal'} ${fontSize}px ${style.fontFamily || 'Arial, sans-serif'}`;

      const lines: string[] = [];
      const availableWidth = maxWidth - padding * 2;

      // Handle long words that need to be broken
      const breakLongWord = (word: string): string[] => {
        const wordLines: string[] = [];
        let currentWordPart = '';

        for (let i = 0; i < word.length; i++) {
          const testPart = currentWordPart + word[i];
          const metrics = context.measureText(testPart);

          if (metrics.width > availableWidth && currentWordPart) {
            wordLines.push(currentWordPart);
            currentWordPart = word[i];
          } else {
            currentWordPart = testPart;
          }
        }

        if (currentWordPart) {
          wordLines.push(currentWordPart);
        }

        return wordLines;
      };

      const words = text.split(' ');
      let currentLine = '';

      words.forEach((word, wordIndex) => {
        // Check if the word itself is too long
        const wordWidth = context.measureText(word).width;

        if (wordWidth > availableWidth) {
          // Push current line if any
          if (currentLine) {
            lines.push(currentLine);
            currentLine = '';
          }
          // Break the long word
          const brokenWords = breakLongWord(word);
          brokenWords.forEach((part, index) => {
            if (index < brokenWords.length - 1) {
              lines.push(part);
            } else {
              currentLine = part;
            }
          });
        } else {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const metrics = context.measureText(testLine);

          if (metrics.width > availableWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }

      return lines.length > 0 ? lines : [''];
    },
    [fontSize, style.fontFamily, style.fontWeight, padding],
  );

  // Memoize wrapped lines to avoid recalculation on every render
  const lines = useMemo(() => {
    // Split text by newlines first, then wrap each paragraph
    const paragraphs = text.split('\n');
    const wrappedLines = paragraphs.flatMap(paragraph =>
      paragraph ? wrapText(paragraph, width) : [''],
    );

    return wrappedLines;
  }, [text, width, wrapText]);

  const lineHeight = fontSize * 1.2;

  // Update text when annotation changes
  useEffect(() => {
    setEditText(annotation.data.text || '');
  }, [annotation.data.text]);

  // Listen for custom event to start editing
  useEffect(() => {
    const handleStartEdit = (e: CustomEvent) => {
      if (e.detail.annotationId === annotation.id) {
        setIsEditing(true);
        setIsEditingText(true);
      }
    };

    window.addEventListener('startTextEdit', handleStartEdit as EventListener);
    return () => {
      window.removeEventListener('startTextEdit', handleStartEdit as EventListener);
    };
  }, [annotation.id, setIsEditingText]);

  // Handle double-click to edit
  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!isInteractive) return;
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
    setIsEditingText(true);
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    const trimmedText = editText.trim();

    // If text is empty, delete the annotation silently
    if (!trimmedText) {
      if (deleteAnnotation) {
        // Check if this is a new annotation (no previous text)
        const isNewAnnotation = !annotation.data.text || annotation.data.text === '';
        await deleteAnnotation(annotation.id);

        // Dispatch event to notify that text editing is complete
        window.dispatchEvent(
          new CustomEvent('textEditComplete', {
            detail: {
              annotationId: annotation.id,
              wasEmpty: isNewAnnotation, // Flag to indicate this was an empty annotation
            },
          }),
        );
      }
      return;
    }

    // Only update if text changed
    if (trimmedText !== annotation.data.text) {
      await updateAnnotation({
        id: annotation.id,
        data: {
          data: {
            ...annotation.data,
            text: trimmedText,
          },
        },
      });
    }
    setIsEditing(false);
    setIsEditingText(false);

    // Dispatch event to notify that text editing is complete
    window.dispatchEvent(
      new CustomEvent('textEditComplete', {
        detail: {
          annotationId: annotation.id,
          wasEmpty: false,
          isNewAnnotation: !annotation.data.text || annotation.data.text === '',
        },
      }),
    );
  };

  // Handle cancel edit
  const handleCancelEdit = async () => {
    const isEmptyAnnotation = !annotation.data.text || annotation.data.text === '';

    // If this is a new empty annotation, delete it when cancelling
    if (isEmptyAnnotation && deleteAnnotation) {
      await deleteAnnotation(annotation.id);
    } else {
      // Otherwise just revert the text
      setEditText(annotation.data.text || '');
    }

    setIsEditing(false);
    setIsEditingText(false);

    // Dispatch event to notify that text editing is complete (even when cancelled)
    // This ensures the AnnotationDrawingOverlay resets its local isEditingText state
    window.dispatchEvent(
      new CustomEvent('textEditComplete', {
        detail: {
          annotationId: annotation.id,
          wasCancelled: true,
          wasEmpty: isEmptyAnnotation,
        },
      }),
    );
  };

  // Handle key down in edit mode
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
    // Prevent space bar from triggering pan mode while editing text
    if (e.code === 'Space') {
      e.stopPropagation();
    }
    // Prevent Delete/Backspace from deleting annotation while editing text
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.stopPropagation();
    }
  };

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: string) => {
      if (!isInteractive || isEditing) return;
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      // Get the SVG element once
      const svg =
        (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
      if (!svg) return;

      // Get initial mouse position in SVG coordinates
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const initialMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

      const originalX = annotation.data.x!;
      const originalY = annotation.data.y!;
      const originalWidth = annotation.data.width || 200;
      const originalHeight = annotation.data.height || 50;

      // Track the last updated values
      let lastX = originalX;
      let lastY = originalY;
      let lastWidth = originalWidth;
      let lastHeight = originalHeight;

      const handleMove = (clientX: number, clientY: number) => {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

        // Calculate delta from initial mouse position
        const deltaX = currentMousePos.x - initialMousePos.x;
        const deltaY = currentMousePos.y - initialMousePos.y;

        let newX = originalX;
        let newY = originalY;
        let newWidth = originalWidth;
        let newHeight = originalHeight;

        switch (handle) {
          case 'nw': // top-left
            newX = originalX + deltaX;
            newY = originalY + deltaY;
            newWidth = originalWidth - deltaX;
            newHeight = originalHeight - deltaY;
            // Ensure minimum size
            if (newWidth < 50) {
              newX = originalX + originalWidth - 50;
              newWidth = 50;
            }
            if (newHeight < 30) {
              newY = originalY + originalHeight - 30;
              newHeight = 30;
            }
            break;
          case 'ne': // top-right
            newY = originalY + deltaY;
            newWidth = originalWidth + deltaX;
            newHeight = originalHeight - deltaY;
            // Ensure minimum size
            if (newWidth < 50) newWidth = 50;
            if (newHeight < 30) {
              newY = originalY + originalHeight - 30;
              newHeight = 30;
            }
            break;
          case 'se': // bottom-right
            newWidth = originalWidth + deltaX;
            newHeight = originalHeight + deltaY;
            // Ensure minimum size
            if (newWidth < 50) newWidth = 50;
            if (newHeight < 30) newHeight = 30;
            break;
          case 'sw': // bottom-left
            newX = originalX + deltaX;
            newWidth = originalWidth - deltaX;
            newHeight = originalHeight + deltaY;
            // Ensure minimum size
            if (newWidth < 50) {
              newX = originalX + originalWidth - 50;
              newWidth = 50;
            }
            if (newHeight < 30) newHeight = 30;
            break;
        }

        // Store the last values
        lastX = newX;
        lastY = newY;
        lastWidth = newWidth;
        lastHeight = newHeight;

        // Update annotation with optimistic update
        updateAnnotation({
          id: annotation.id,
          data: {
            data: {
              ...annotation.data,
              x: newX,
              y: newY,
              width: newWidth,
              height: newHeight,
            },
          },
          skipHistory: true,
        });
      };

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        handleMove(e.clientX, e.clientY);
      };

      const handleMouseUp = () => {
        setIsResizing(false);

        // Final update with history if values changed
        if (
          lastX !== originalX ||
          lastY !== originalY ||
          lastWidth !== originalWidth ||
          lastHeight !== originalHeight
        ) {
          updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                x: lastX,
                y: lastY,
                width: lastWidth,
                height: lastHeight,
              },
            },
            skipHistory: false,
          });
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };

      const handleTouchEnd = () => {
        handleMouseUp();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    },
    [annotation, updateAnnotation, isInteractive, isEditing],
  );

  // Handle rotation
  const handleRotationStart = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive || isEditing) return;
      e.stopPropagation();
      e.preventDefault();
      setIsRotating(true);

      // Get the SVG element
      const svg =
        (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
      if (!svg) return;

      // Calculate center of text box
      const centerX = x + width / 2;
      const centerY = y + height / 2;

      // Get initial angle
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
      const initialAngle = Math.atan2(svgP.y - centerY, svgP.x - centerX) * (180 / Math.PI);
      const startRotation = rotationAngle; // Use current rotationAngle, not rotation prop

      // Store the latest rotation value
      let latestRotation = startRotation;

      const handleMove = (clientX: number, clientY: number, shiftKey: boolean = false) => {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentPos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

        // Calculate current angle
        const currentAngle =
          Math.atan2(currentPos.y - centerY, currentPos.x - centerX) * (180 / Math.PI);
        const deltaAngle = currentAngle - initialAngle;
        let newRotation = (startRotation + deltaAngle) % 360;

        // Normalize to 0-360
        if (newRotation < 0) newRotation += 360;

        // Snap to 45-degree increments unless Shift is held
        if (!shiftKey) {
          const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
          const snapThreshold = 5; // degrees

          for (const snapAngle of snapAngles) {
            if (Math.abs(newRotation - snapAngle) < snapThreshold) {
              newRotation = snapAngle % 360;
              break;
            }
          }
        }

        latestRotation = newRotation;
        setRotationAngle(newRotation);
      };

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        handleMove(e.clientX, e.clientY, e.shiftKey);
      };

      const handleEnd = async () => {
        setIsRotating(false);

        // Final update with history using the latest rotation value
        try {
          await updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                rotation: latestRotation,
              },
            },
            skipHistory: false,
          });
        } catch (error) {
          logger.error('Failed to save rotation:', error);
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };

      const handleTouchEnd = () => {
        handleEnd();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    },
    [
      annotation.id,
      annotation.data,
      updateAnnotation,
      isInteractive,
      isEditing,
      x,
      y,
      width,
      height,
      rotationAngle,
    ],
  );

  // Track double tap for touch devices
  const lastTapRef = useRef<number>(0);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!isInteractive) return;

      const currentTime = new Date().getTime();
      const tapLength = currentTime - lastTapRef.current;

      if (tapLength < 300 && tapLength > 0) {
        // Double tap detected
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        setIsEditingText(true);
      } else {
        // Single tap - trigger selection
        e.preventDefault();
        e.stopPropagation();
        onClick(e as any);

        // Also pass to parent if needed
        if (onTouchEnd) {
          onTouchEnd(e);
        }
      }

      lastTapRef.current = currentTime;
    },
    [isInteractive, onClick, onTouchEnd],
  );

  // Calculate center for rotation
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  return (
    <g
      data-annotation-id={annotation.id}
      className={isInteractive ? 'cursor-pointer' : ''}
      transform={`rotate(${rotationAngle} ${centerX} ${centerY})`}
    >
      {/* Background rectangle for hit area and selection */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={
          isSelected || isHovered || isEditing
            ? 'rgba(37, 99, 235, 0.1)'
            : 'rgba(255, 255, 255, 0.01)'
        }
        stroke={isSelected ? '#2563eb' : isHovered ? '#6b7280' : 'transparent'}
        strokeWidth={2 / scale}
        strokeDasharray={isSelected ? `${4 / scale} ${2 / scale}` : undefined}
        pointerEvents={isInteractive ? 'auto' : 'none'}
        onClick={onClick}
        onMouseDown={isResizing ? undefined : isEditing ? undefined : onMouseDown}
        onTouchStart={isResizing ? undefined : isEditing ? undefined : onTouchStart}
        onTouchEnd={isResizing ? undefined : isEditing ? undefined : handleTouchEnd}
        onTouchMove={isResizing ? undefined : isEditing ? undefined : onTouchMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDoubleClick={handleDoubleClick}
      />

      {/* Text content or edit input */}
      {isEditing ? (
        <foreignObject x={x} y={y} width={width} height={height}>
          <textarea
            ref={inputRef}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onBlur={() => {
              // Always save on blur - this handles clicking outside
              handleSaveEdit();
            }}
            onKeyDown={handleKeyDown}
            style={{
              width: '100%',
              height: '100%',
              padding: `${padding}px`,
              border: 'none',
              outline: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: style.color || '#000000',
              fontSize: `${fontSize}px`,
              fontFamily: style.fontFamily || 'Arial, sans-serif',
              fontWeight: style.fontWeight || 'normal',
              resize: 'none',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              lineHeight: lineHeight / fontSize,
            }}
            placeholder="Enter text..."
          />
        </foreignObject>
      ) : (
        <>
          {/* Clip text to bounds */}
          <defs>
            <clipPath id={`text-clip-${annotation.id}`}>
              <rect x={x} y={y} width={width} height={height} />
            </clipPath>
          </defs>
          <text
            x={x + padding}
            y={y + padding + fontSize}
            fill={style.color || '#000000'}
            fontSize={fontSize}
            fontFamily={style.fontFamily || 'Arial, sans-serif'}
            fontWeight={style.fontWeight || 'normal'}
            opacity={style.opacity || 1}
            pointerEvents="none"
            clipPath={`url(#text-clip-${annotation.id})`}
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
          >
            {lines.map((line, index) => {
              // Calculate if this line is visible within the height
              const lineY = index * lineHeight;
              if (lineY + fontSize > height - padding * 2) {
                return null; // Skip lines that would overflow
              }

              return (
                <tspan
                  key={index}
                  x={x + padding}
                  dy={index === 0 ? 0 : lineHeight}
                  textAnchor={
                    style.textAlign === 'center'
                      ? 'middle'
                      : style.textAlign === 'right'
                        ? 'end'
                        : 'start'
                  }
                  {...(style.textAlign === 'center' && { x: x + width / 2 })}
                  {...(style.textAlign === 'right' && { x: x + width - padding })}
                >
                  {line}
                </tspan>
              );
            })}
          </text>
        </>
      )}

      {/* Hover effect - clean dashed outline */}
      {isHovered && !isSelected && !isEditing && (
        <rect
          x={x - 2 / scale}
          y={y - 2 / scale}
          width={width + 4 / scale}
          height={height + 4 / scale}
          fill="none"
          stroke="#6366f1"
          strokeWidth={1.5 / scale}
          strokeOpacity={0.7}
          strokeDasharray={`${4 / scale} ${2 / scale}`}
          rx={2 / scale}
          pointerEvents="none"
        />
      )}

      {/* Rotation handle when selected and not editing */}
      {isSelected && !isEditing && isInteractive && (
        <>
          {/* Rotation handle - positioned above the text box */}
          <g>
            {/* Line from center to rotation handle */}
            <line
              x1={centerX}
              y1={y - 40 / scale}
              x2={centerX}
              y2={y}
              stroke="#2563eb"
              strokeWidth={2 / scale}
              pointerEvents="none"
            />

            {/* Invisible larger hit area for rotation handle */}
            <circle
              cx={centerX}
              cy={y - 40 / scale}
              r={16 / scale}
              fill="transparent"
              pointerEvents="all"
              style={{ cursor: 'grab' }}
              onMouseDown={handleRotationStart}
              onTouchStart={e => {
                e.preventDefault();
                e.stopPropagation();
                wrapTouchEvent(handleRotationStart)(e);
              }}
            />

            {/* Rotation handle button - using foreignObject to match fixtures/LV products */}
            <foreignObject
              x={centerX - 10 / scale}
              y={y - 40 / scale - 10 / scale}
              width={20 / scale}
              height={20 / scale}
              style={{ overflow: 'visible' }}
            >
              <div
                className="bg-white rounded-full shadow-lg border border-blue-400/80 cursor-grab hover:scale-110 hover:border-blue-500 hover:shadow-xl transition-all flex items-center justify-center"
                style={{
                  width: '20px',
                  height: '20px',
                }}
              >
                {/* Rotation icon - elegant curved arrow (exact match to fixtures/LV products) */}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  className="text-blue-500"
                >
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.0902 3 17.7902 4.58073 19.3499 7"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                  />
                  <path
                    d="M17 7L19.5 7L19.5 4.5"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </foreignObject>
          </g>

          {/* Angle indicator when rotating */}
          {isRotating && (
            <g>
              {/* Background for angle text */}
              <rect
                x={centerX - 25 / scale}
                y={centerY - 10 / scale}
                width={50 / scale}
                height={20 / scale}
                rx={4 / scale}
                fill="rgba(37, 99, 235, 0.9)"
                pointerEvents="none"
              />
              {/* Angle text */}
              <text
                x={centerX}
                y={centerY + 3 / scale}
                fill="white"
                fontSize={12 / scale}
                fontWeight="600"
                textAnchor="middle"
                pointerEvents="none"
              >
                {Math.round(rotationAngle || rotation)}°
              </text>
            </g>
          )}
        </>
      )}

      {/* Resize handles when selected and not editing */}
      {isSelected && !isEditing && isInteractive && (
        <>
          {/* Corner resize handles */}
          {[
            { x: x, y: y, handle: 'nw', cursor: 'nwse-resize' },
            { x: x + width, y: y, handle: 'ne', cursor: 'nesw-resize' },
            { x: x + width, y: y + height, handle: 'se', cursor: 'nwse-resize' },
            { x: x, y: y + height, handle: 'sw', cursor: 'nesw-resize' },
          ].map((handle, index) => (
            <rect
              key={index}
              x={handle.x - 4 / scale}
              y={handle.y - 4 / scale}
              width={8 / scale}
              height={8 / scale}
              fill="#2563eb"
              stroke="white"
              strokeWidth={1 / scale}
              style={{ cursor: handle.cursor }}
              pointerEvents="all"
              onMouseDown={e => handleResizeStart(e, handle.handle)}
              onTouchStart={e => {
                if (e.touches.length === 1) {
                  e.preventDefault();
                  const syntheticEvent = {
                    ...e,
                    clientX: e.touches[0].clientX,
                    clientY: e.touches[0].clientY,
                    preventDefault: () => e.preventDefault(),
                    stopPropagation: () => e.stopPropagation(),
                    target: e.target,
                    currentTarget: e.currentTarget,
                  } as unknown as React.MouseEvent;
                  handleResizeStart(syntheticEvent, handle.handle);
                }
              }}
            />
          ))}
        </>
      )}
    </g>
  );
}
